import { IEmailTemplate } from '@/typings';
import { Form, useForm, useFormState, useField } from 'react-final-form';
import arrayMutators from 'final-form-arrays';
import React, { useMemo, useEffect, useState } from 'react';
import { BlocksProvider } from '..//BlocksProvider';
import { HoverIdxProvider } from '../HoverIdxProvider';
import { ListingPickerProvider } from '../ListingPickerProvider';
import { PropsProvider, PropsProviderProps } from '../PropsProvider';
import { RecordProvider } from '../RecordProvider';
import { ScrollProvider } from '../ScrollProvider';
import { Config, FormApi, FormState } from 'final-form';
import setFieldTouched from 'final-form-set-field-touched';
import { FocusBlockLayoutProvider } from '../FocusBlockLayoutProvider';
import { LanguageProvider } from '../LanguageProvider';
import { overrideErrorLog, restoreErrorLog } from '@/utils/logger';
import { initializeTemplateReconciliation, reconcileTemplateForSave } from '@/utils/reconcileTemplate';

export interface EmailEditorProviderProps<T extends IEmailTemplate = any>
  extends Omit<PropsProviderProps, 'children'> {
  data: T;
  children: (
    props: FormState<T>,
    helper: FormApi<IEmailTemplate, Partial<IEmailTemplate>>,
  ) => React.ReactNode;
  onSubmit?: Config<IEmailTemplate, Partial<IEmailTemplate>>['onSubmit'];
  validationSchema?: Config<IEmailTemplate, Partial<IEmailTemplate>>['validate'];
}

export const EmailEditorProvider = <T extends any>(
  props: EmailEditorProviderProps & T,
) => {
  const { data, children, onSubmit = () => {}, validationSchema } = props;

  const initializedData = useMemo(() => {
    return initializeTemplateReconciliation(data as IEmailTemplate);
  }, [data]);

  const initialValues = useMemo(() => {
    return {
      subject: initializedData.subject,
      subTitle: initializedData.subTitle,
      content: initializedData.content,
      reconciliation: initializedData.reconciliation,
    };
  }, [initializedData]);

  const handleSubmit = useMemo<Config<IEmailTemplate, Partial<IEmailTemplate>>['onSubmit']>(
    () => (values, form, callback) => {
      const nextValues = reconcileTemplateForSave(values as IEmailTemplate);
      return onSubmit(nextValues, form, callback);
    },
    [onSubmit],
  );

  useEffect(() => {
    overrideErrorLog();
    return () => {
      restoreErrorLog();
    };
  }, []);

  if (!initialValues.content) return null;

  return (
    <Form<IEmailTemplate>
      initialValues={initialValues}
      onSubmit={handleSubmit}
      enableReinitialize
      validate={validationSchema}
      mutators={{ ...arrayMutators, setFieldTouched: setFieldTouched as any }}
      subscription={{ submitting: true, pristine: true, initialValues: true }}
    >
      {(formState) => {
        // Ensure form is initialized before rendering children
        if (!formState.initialValues) {
          return null;
        }

        return (
          <>
            <PropsProvider {...props}>
              <LanguageProvider locale={props.locale}>
                {/*
                  NOTE: PreviewEmailProvider was removed from here to fix performance issues.
                  It was causing excessive re-renders (6+ per 20 seconds) and unnecessary MJML compilation.

                  The preview modal (PreviewModal) now generates its own preview on-demand.

                  WARNING: If DesktopEmailPreview or MobileEmailPreview are ever re-enabled,
                  they will need to be refactored as they currently depend on PreviewEmailProvider.
                  See: /src/bay-email-editor/packages/bay-email-editor/src/components/EmailEditor/components/DesktopEmailPreview/index.tsx
                  See: /src/bay-email-editor/packages/bay-email-editor/src/components/EmailEditor/components/MobileEmailPreview/index.tsx
                */}
                <RecordProvider>
                  <BlocksProvider>
                    <HoverIdxProvider>
                      <ListingPickerProvider>
                        <ScrollProvider>
                          <FocusBlockLayoutProvider>
                            <FormWrapper children={children} />
                          </FocusBlockLayoutProvider>
                        </ScrollProvider>
                      </ListingPickerProvider>
                    </HoverIdxProvider>
                  </BlocksProvider>
                </RecordProvider>
              </LanguageProvider>
            </PropsProvider>
            <RegisterFields />
          </>
        );
      }}
    </Form>
  );
};

function FormWrapper({ children }: { children: EmailEditorProviderProps['children'] }) {
  const data = useFormState<IEmailTemplate>();
  const helper = useForm<IEmailTemplate>();
  return <>{children(data, helper)}</>;
}

// final-form bug https://github.com/final-form/final-form/issues/169

const RegisterFields = React.memo(() => {
  const { touched } = useFormState<IEmailTemplate>();
  const [touchedMap, setTouchedMap] = useState<{ [key: string]: boolean }>({});

  useEffect(() => {
    if (touched) {
      Object.keys(touched)
        .filter(key => touched[key])
        .forEach(key => {
          setTouchedMap(obj => {
            obj[key] = true;
            return { ...obj };
          });
        });
    }
  }, [touched]);

  return (
    <>
      {Object.keys(touchedMap).map(key => {
        return (
          <RegisterField
            key={key}
            name={key}
          />
        );
      })}
    </>
  );
});

function RegisterField({ name }: { name: string }) {
  useField(name);
  return <></>;
}
